import React, { useMemo } from 'react';
import { AlertTriangle, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, Badge } from '@components/Card';
import { formatCnpj } from '@/types/company';
import type { CarrierServiceOption } from '@/types/paymentRouting';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import { CarrierBlocksEditor } from './CarrierBlocksEditor';
import {
  buildMonthMeter,
  capInputDisplay,
  capInputFromTyped,
  formatShare,
  MAX_WEIGHT,
  MIN_WEIGHT,
  toWeightedInput,
  totalActiveWeight,
  WeightedDraftRule,
  WeightedValidation,
} from './weightedRoutingUtils';

interface WeightedRoutingPanelProps {
  rules: WeightedDraftRule[];
  carrierServices: CarrierServiceOption[];
  carrierLoadError: string | null;
  validation: WeightedValidation;
  onChange: (companyId: number, changes: Partial<WeightedDraftRule>) => void;
  renderCities: (companyId: number) => React.ReactNode;
}

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export const WeightedRoutingPanel: React.FC<WeightedRoutingPanelProps> = ({
  rules, carrierServices, carrierLoadError, validation, onChange, renderCities,
}) => {
  const inputs = useMemo(() => rules.map(toWeightedInput), [rules]);
  const totalWeight = totalActiveWeight(inputs);
  const meter = useMemo(
    () => buildMonthMeter(inputs.map((inp, i) => ({ ...inp, captured: rules[i].captured_this_month ?? 0 }))),
    [inputs, rules],
  );

  return (
    <div className="space-y-4">
      {validation.errors.map((e, i) => (
        <div key={`e${i}`} className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-700">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
          <span>{e}</span>
        </div>
      ))}
      {validation.warnings.map((w, i) => (
        <div key={`w${i}`} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
          <span>{w}</span>
        </div>
      ))}

      {/* Medidor do mês */}
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Scale size={16} className="text-emerald-600" />
              Balanço do mês (recebido)
            </span>
          }
          subtitle="Cartão capturado + PIX QR Code pago + transferência confirmada no CRM, no mês corrente (horário de Brasília). É a base do rodízio e do teto; zera no dia 1."
        />
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 text-left">Empresa</th>
                  <th className="px-4 py-2 text-right">Recebido</th>
                  <th className="px-4 py-2 text-right">% real</th>
                  <th className="px-4 py-2 text-right">% alvo</th>
                  <th className="px-4 py-2 text-right">Saldo do teto</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r, i) => {
                  const m = meter[i];
                  return (
                    <tr key={r.company_id} className="border-b border-slate-100">
                      <td className="px-4 py-2 text-slate-700">
                        {r.company_name}
                        {!r.enabled && <span className="ml-2 text-xs text-slate-400">(inativa)</span>}
                      </td>
                      <td className="px-4 py-2 text-right">{formatCurrencyBRL(m.captured)}</td>
                      <td className="px-4 py-2 text-right">{r.enabled ? pct(m.actual_pct) : '—'}</td>
                      <td className="px-4 py-2 text-right">{r.enabled ? pct(m.target_pct) : '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {m.cap_remaining == null ? (
                          <span className="text-slate-400">sem teto</span>
                        ) : (
                          <span className={m.cap_remaining <= 0 ? 'text-red-600 font-medium' : ''}>
                            {formatCurrencyBRL(m.cap_remaining)}
                            {m.cap_used_pct != null && (
                              <span className="text-xs text-slate-500"> ({pct(m.cap_used_pct)} usado)</span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {rules.map((rule, i) => {
        const inp = inputs[i];
        return (
          <Card key={rule.company_id}>
            <CardContent>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-700">{rule.company_name}</h3>
                  <span className="text-xs text-slate-500 font-mono">{formatCnpj(rule.company_document)}</span>
                  {inp.cap_cents == null && rule.enabled && <Badge variant="success">Sem teto</Badge>}
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    checked={rule.enabled}
                    onChange={e => onChange(rule.company_id, { enabled: e.target.checked })}
                  />
                  Ativa
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Peso</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={rule.weight}
                    onChange={e => onChange(rule.company_id, { weight: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {MIN_WEIGHT} a {MAX_WEIGHT} · participação alvo: {rule.enabled ? formatShare(inp.weight, totalWeight) : '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teto mensal (R$)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Sem teto"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={capInputDisplay(rule.monthly_revenue_cap)}
                    onChange={e => onChange(rule.company_id, { monthly_revenue_cap: capInputFromTyped(e.target.value) })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Vazio = sem teto. O teto nunca é ultrapassado: o pedido que estouraria vai para outra empresa.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">% de disparo</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={rule.cap_trigger_percent}
                    onChange={e => {
                      const v = Number(e.target.value);
                      onChange(rule.company_id, { cap_trigger_percent: isNaN(v) ? 100 : v });
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Teto efetivo = teto × %{inp.cap_cents != null ? ` = ${formatCurrencyBRL(inp.cap_cents / 100)}` : ''}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <CarrierBlocksEditor
                  options={carrierServices}
                  blocks={rule.carrier_blocks}
                  loadError={carrierLoadError}
                  onChange={blocks => onChange(rule.company_id, { carrier_blocks: blocks })}
                />
                {renderCities(rule.company_id)}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                O bloqueio de transportadora vale acima do peso e do teto: um pedido com transportadora bloqueada
                aqui vai para outra empresa, mesmo que ela já esteja no teto. Também bloqueia a transportadora na
                cotação de frete, no pedido de envio e na emissão da NF desta empresa.
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
